<template>
  <div class="step-col-mapping-v2" data-testid="step-col-mapping-v2__view">
    <!-- ─── Section 1: File Type Upload Table ─── -->
    <div class="step-col-mapping-v2__section" data-testid="step-col-mapping-v2__file-upload-section">
      <div class="step-col-mapping-v2__section-title">Section 1 — Upload Files</div>
      <div class="step-col-mapping-v2__section-subtitle">
        Upload one file for each file type. Name and key are auto-generated from the file name.
      </div>

      <v-simple-table dense class="step-col-mapping-v2__upload-table">
        <thead>
          <tr>
            <th class="text-center" style="width: 3rem;">#</th>
            <th style="min-width: 12rem;">File Type Name</th>
            <th style="min-width: 6rem;">Ignore Rows</th>
            <th style="min-width: 6rem;">Ignore Cols</th>
            <th style="min-width: 7rem;">Ignore Footer Rows</th>
            <th style="min-width: 10rem;">Blank Headers</th>
            <th class="text-center" style="width: 6rem;">Keep Duplicate</th>
            <th style="min-width: 10rem;">Browse</th>
            <th class="text-center" style="width: 5rem;">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(entry, idx) in fileTypeEntries"
            :key="'ft-' + idx"
            data-testid="step-col-mapping-v2__upload-row"
          >
            <td class="text-center">{{ idx + 1 }}</td>
            <td>
              <v-text-field
                v-model="entry.name"
                dense
                hide-details
                outlined
                placeholder="File type name"
                data-testid="step-col-mapping-v2__file-type-name"
              />
            </td>
            <td style="min-width: 6rem;">
              <v-text-field
                v-model.number="entry.ignorerows"
                type="number"
                min="0"
                dense
                hide-details
                outlined
                @change="onEntrySettingChanged(idx)"
                data-testid="step-col-mapping-v2__ignore-rows"
              />
            </td>
            <td style="min-width: 6rem;">
              <v-text-field
                v-model.number="entry.ignorecolumns"
                type="number"
                min="0"
                dense
                hide-details
                outlined
                @change="onEntrySettingChanged(idx)"
                data-testid="step-col-mapping-v2__ignore-cols"
              />
            </td>
            <td style="min-width: 7rem;">
              <v-text-field
                v-model.number="entry.ignorefooterrows"
                type="number"
                min="0"
                dense
                hide-details
                outlined
                @change="onEntrySettingChanged(idx)"
                data-testid="step-col-mapping-v2__footer-rows"
              />
            </td>
            <td style="min-width: 10rem;">
              <v-select
                v-model="entry.blankcolumnheaders"
                :items="blankheadersItem"
                dense
                hide-details
                outlined
                @change="onEntrySettingChanged(idx)"
                data-testid="step-col-mapping-v2__blank-headers"
              />
            </td>
            <td class="text-center" style="width: 6rem;">
              <v-simple-checkbox
                v-model="entry.keepduplicatecolumns"
                color="primary"
                @input="onEntrySettingChanged(idx)"
                data-testid="step-col-mapping-v2__keep-dupes"
              />
            </td>
            <td>
              <div class="d-flex align-center" style="gap: var(--space-2);">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="triggerFileInput(idx)"
                  data-testid="step-col-mapping-v2__browse-btn"
                >
                  <v-icon left small>mdi-paperclip</v-icon>
                  Browse
                </v-btn>
                <input
                  :ref="'fileInput_' + idx"
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.xlsb,.csv"
                  class="d-none"
                  @change="onFileSelected($event, idx)"
                />
                <v-chip
                  v-if="entry.fileName"
                  small
                  label
                  close
                  @click:close="removeFileType(idx)"
                  data-testid="step-col-mapping-v2__file-chip"
                >
                  <v-icon left small>mdi-file</v-icon>
                  {{ entry.fileName }}
                </v-chip>
              </div>
            </td>
            <td class="text-center">
              <v-icon
                v-if="entry.status === 'success'"
                color="green"
                small
              >mdi-check-circle</v-icon>
              <v-icon
                v-else-if="entry.status === 'error'"
                color="orange"
                small
              >mdi-alert-circle</v-icon>
              <v-icon
                v-else
                color="grey"
                small
              >mdi-minus-circle-outline</v-icon>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>

    <!-- ─── Section 2: Base File Selection ─── -->
    <div class="step-col-mapping-v2__section" data-testid="step-col-mapping-v2__base-selection-section">
      <div class="step-col-mapping-v2__section-title">Section 2 — Select Base File</div>
      <div class="step-col-mapping-v2__section-subtitle">
        Choose which uploaded file type provides the base columns for the mapping table.
      </div>
      <v-row>
        <v-col cols="12" sm="4" class="my-0 py-0">
          <lb-dropdown
            label="Base File"
            v-model="selectedBaseFileTypeKey"
            :items="baseFileOptions"
            itemtext="text"
            itemvalue="value"
            @change="onBaseFileChanged"
            data-testid="step-col-mapping-v2__base-file-dropdown"
          />
        </v-col>
      </v-row>
    </div>

    <!-- ─── Section 3: Mapping Table ─── -->
    <div
      v-if="selectedBaseFileTypeKey && mappingRows.length > 0"
      class="step-col-mapping-v2__section"
      data-testid="step-col-mapping-v2__mapping-section"
    >
      <div class="step-col-mapping-v2__section-title">Section 3 — Column Mapping</div>
      <div class="step-col-mapping-v2__section-subtitle">
        Map columns from each file type to the base columns. Select "{{ NULL_MAPPING_DISPLAY }}"
        to skip a column for a file type.
      </div>

      <div class="step-col-mapping-v2__mapping-table-wrapper" data-testid="step-col-mapping-v2__mapping-table">
        <v-simple-table dense class="step-col-mapping-v2__mapping-table">
          <thead>
            <tr>
              <th class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--drag"></th>
              <th class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--index">#</th>
              <th class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--base">Base Column</th>
              <th class="step-col-mapping-v2__scroll-col" style="min-width: 8rem;">Data Type</th>
              <th class="step-col-mapping-v2__scroll-col" style="min-width: 10rem;">User Column Mapping</th>
              <th
                v-for="ft in fileTypeEntries"
                :key="'th-' + ft.key"
                class="step-col-mapping-v2__scroll-col"
                style="min-width: 12rem;"
              >
                {{ ft.name || ft.key }}
              </th>
              <th class="step-col-mapping-v2__scroll-col" style="width: 3rem;"></th>
            </tr>
          </thead>
           <draggable
            v-model="mappingRows"
            tag="tbody"
            handle=".step-col-mapping-v2__drag-handle"
            @end="cleanPrimaryKeys"
          >
            <tr
              v-for="(row, rowIdx) in mappingRows"
              :key="'mr-' + row.id"
              data-testid="step-col-mapping-v2__mapping-row"
            >
              <!-- Sticky: Drag Handle -->
              <td class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--drag text-center">
                <v-icon small class="step-col-mapping-v2__drag-handle">mdi-drag-vertical</v-icon>
              </td>
              <!-- Sticky: Row Index -->
              <td class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--index text-center">
                {{ rowIdx + 1 }}
              </td>
              <!-- Sticky: Base Column -->
              <td class="step-col-mapping-v2__sticky-col step-col-mapping-v2__sticky-col--base">
                <v-text-field
                  v-model="row.baseColumn"
                  dense
                  hide-details
                  outlined
                  placeholder="Column name"
                  @input="onBaseColumnRenamed(rowIdx)"
                  data-testid="step-col-mapping-v2__base-col-input"
                />
              </td>
              <!-- Scrollable: Data Type -->
              <td class="step-col-mapping-v2__scroll-col">
                <v-select
                  v-model="row.dataType"
                  :items="dataTypeOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                  outlined
                  data-testid="step-col-mapping-v2__data-type-select"
                />
              </td>
              <!-- Scrollable: User Column Mapping -->
              <td class="step-col-mapping-v2__scroll-col">
                <lb-tagsInput
                  :multiple="false"
                  :multiplechips="false"
                  v-model="row.userColumnMappingLabel"
                  :items="getAvailableUserColMapOptions(rowIdx)"
                  itemtext="text"
                  itemvalue="value"
                  :hidedetails="true"
                  data-testid="step-col-mapping-v2__user-col-map-select"
                />
              </td>
              <!-- Scrollable: File Type Columns -->
              <td
                v-for="ft in fileTypeEntries"
                :key="'td-' + ft.key + '-' + rowIdx"
                class="step-col-mapping-v2__scroll-col"
              >
                <v-autocomplete
                  :value="row.fileTypeMappings[ft.key]"
                  @input="onFileTypeMappingChanged(rowIdx, ft.key, $event)"
                  :items="getAvailableHeaders(ft.key, rowIdx)"
                  dense
                  hide-details
                  outlined
                  data-testid="step-col-mapping-v2__file-type-col-select"
                />
              </td>
              <!-- Remove Row -->
              <td class="step-col-mapping-v2__scroll-col text-center">
                <v-btn
                  icon
                  x-small
                  @click="removeRow(rowIdx)"
                  data-testid="step-col-mapping-v2__remove-row-btn"
                >
                  <v-icon small color="red lighten-1">mdi-trash-can-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </draggable>
        </v-simple-table>
      </div>

      <v-btn
        small
        text
        color="primary"
        class="mt-2"
        @click="addRow"
        data-testid="step-col-mapping-v2__add-row-btn"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add Column
      </v-btn>
    </div>

    <!-- ─── Section 4: Primary Key Selection ─── -->
    <div
      v-if="selectedBaseFileTypeKey && mappingRows.length > 0"
      class="step-col-mapping-v2__section"
      data-testid="step-col-mapping-v2__primary-keys-section"
    >
      <div class="step-col-mapping-v2__section-title">Section 4 — Primary Keys</div>
      <div class="step-col-mapping-v2__section-subtitle">
        Select and order columns to use as primary keys for deduplication and matching.
      </div>
      <v-row>
        <v-col cols="12" sm="6" class="my-0 py-0">
          <v-autocomplete
            v-model="selectedPrimaryKeys"
            :items="baseColumnLabels"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            label="Primary Keys"
            data-testid="step-col-mapping-v2__primary-keys-select"
          />
        </v-col>
      </v-row>
    </div>

    <!-- ─── Actions ─── -->
    <div class="step-col-mapping-v2__actions">
      <v-btn
        color="primary"
        @click="onBack"
        data-testid="step-col-mapping-v2__back-btn"
      >
        <p>Back</p>
      </v-btn>
      <v-btn
        color="primary"
        @click="onNextStep"
        data-testid="step-col-mapping-v2__next-btn"
      >
        <p>Next Step</p>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { DATA_TYPES, USER_COL_MAP_OPTIONS, FILE_STATUS, NULL_MAPPING_VALUE, NULL_MAPPING_DISPLAY, validateLabel } from './utils/constants';
import { generateKey, uniqueKey, inferDataType, autoMatchHeader, fileNameToDisplayName } from './utils/helpers';
import draggable from 'vuedraggable';

export default {
  name: 'StepColumnMappingV2',
  components: { draggable },
  props: {
    numberOfFileTypes: {
      type: Number,
      required: true,
      default: 2,
    },
    ignoreRow: {
      type: Number,
      default: 0,
    },
    ignoreColums: {
      type: Number,
      default: 0,
    },
    blankheaders: {
      type: String,
      default: 'left',
    },
    duplicateColumns: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      NULL_MAPPING_DISPLAY,
      dataTypeOptions: DATA_TYPES,
      userColMapOptions: USER_COL_MAP_OPTIONS,
      fileTypeEntries: [],
      selectedBaseFileTypeKey: '',
      mappingRows: [],
      selectedPrimaryKeys: [],
      blankheadersItem: ['left', 'right', 'top', 'bottom'],
    };
  },
  computed: {
    /** Dropdown items for Section 2 base file picker — only successfully parsed files */
    baseFileOptions() {
      return this.fileTypeEntries
        .filter((ft) => ft.status === FILE_STATUS.SUCCESS)
        .map((ft) => ({ text: ft.name || ft.key, value: ft.key }));
    },
    /** Current base column labels for primary key dropdown */
    baseColumnLabels() {
      return this.mappingRows
        .filter((r) => r.baseColumn && r.baseColumn.trim() !== '')
        .map((r) => r.baseColumn);
    },
  },
  watch: {
    numberOfFileTypes(newCount, oldCount) {
      if (newCount === oldCount) return;
      this.resizeFileTypeEntries(newCount);
    },
  },
  created() {
    this.initFileTypeEntries();
  },
  methods: {
    // ──────────────────────────────────────────────
    // Initialisation
    // ──────────────────────────────────────────────
    createEmptyEntry() {
      return {
        key: '',
        name: '',
        fileName: '',
        headers: [],
        status: FILE_STATUS.PENDING,
        rawData: [],
        ignorerows: 0,
        ignorecolumns: 0,
        ignorefooterrows: 0,
        blankcolumnheaders: 'left',
        removeblankrows: true,
        keepduplicatecolumns: false,
      };
    },

    initFileTypeEntries() {
      this.fileTypeEntries = [];
      for (let i = 0; i < this.numberOfFileTypes; i++) {
        this.fileTypeEntries.push(this.createEmptyEntry());
      }
    },

    /**
     * Adjust fileTypeEntries when numberOfFileTypes changes.
     * Keeps existing entries (up to newCount) and appends/trims as needed.
     */
    resizeFileTypeEntries(newCount) {
      const current = this.fileTypeEntries.length;
      if (newCount > current) {
        for (let i = current; i < newCount; i++) {
          this.fileTypeEntries.push(this.createEmptyEntry());
        }
      } else if (newCount < current) {
        // Remove entries from the end; if a removed entry was the base file, clear mapping
        const removed = this.fileTypeEntries.splice(newCount);
        const removedKeys = removed.map((e) => e.key).filter(Boolean);
        if (removedKeys.includes(this.selectedBaseFileTypeKey)) {
          this.selectedBaseFileTypeKey = '';
          this.mappingRows = [];
          this.selectedPrimaryKeys = [];
        }
      }
      this.$forceUpdate();
    },

    // ──────────────────────────────────────────────
    // Section 1: File Upload
    // ──────────────────────────────────────────────
    triggerFileInput(idx) {
      const ref = this.$refs['fileInput_' + idx];
      if (ref && ref[0]) {
        ref[0].click();
      } else if (ref) {
        ref.click();
      }
    },

    async onFileSelected(event, idx) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      const entry = this.fileTypeEntries[idx];
      entry.fileName = file.name;
      entry.status = FILE_STATUS.PENDING;

      // Auto-generate name and key from file name
      const displayName = fileNameToDisplayName(file.name);
      entry.name = displayName;
      const existingKeys = this.fileTypeEntries
        .filter((_, i) => i !== idx)
        .map((ft) => ft.key)
        .filter(Boolean);
      entry.key = uniqueKey(generateKey(file.name), existingKeys);

      try {
        const ext = (file.name || '').split('.').pop().toLowerCase();
        if (
          ['xls', 'xlsx', 'xlsb'].includes(ext) &&
          file.size > 1024 * 1024 * 2
        ) {
          throw new Error('File with size > 2MB should be in csv format');
        }

        let data;
        if (['xlsx', 'xls', 'xlsm', 'xlsb'].includes(ext)) {
          data = await this.$nova.readExcelFile(file, '', {
            removeblankrows: false,
            columncount: 0,
          }, true);
        } else if (ext === 'csv') {
          data = await this.$nova.readCSV(file, {
            removeblankrows: false,
            columncount: 0,
          });
        } else {
          throw new Error('Upload a valid file (.xlsx, .xls, .xlsm, .xlsb, .csv)');
        }

        entry.rawData = data;
        entry.headers = this.extractHeaders(data, entry);
        entry.status = FILE_STATUS.SUCCESS;

        this.$forceUpdate();
      } catch (err) {
        entry.status = FILE_STATUS.ERROR;
        this.$store.commit('sbError', err.message || 'Failed to parse file');
        console.error('[StepColumnMappingV2]: onFileSelected error', err);
        this.$forceUpdate();
      }

      // Reset file input so the same file can be re-selected
      event.target.value = '';
    },

    extractHeaders(data, entry) {
      if (!data || data.length === 0) return [];

      const ignoreRow = entry ? (parseInt(entry.ignorerows, 10) || 0) : 0;
      const ignoreColums = entry ? (parseInt(entry.ignorecolumns, 10) || 0) : 0;
      const blankheaders = entry ? (entry.blankcolumnheaders || 'left') : 'left';
      const duplicateColumns = entry ? !!entry.keepduplicatecolumns : false;

      const selectRow = [...(data[ignoreRow] || [])];

      // Determine which adjacent row to use for blank header fill
      let copyIndex = 0;
      if (blankheaders === 'bottom') copyIndex = 1;
      else if (blankheaders === 'top') copyIndex = -1;
      const blankRow = [...(data[ignoreRow + copyIndex] || [])];

      const selectColumsOne = selectRow.slice(ignoreColums) || [];
      const blankColum = blankRow.slice(ignoreColums) || [];

      // Fill blank headers using the chosen strategy
      let finalColum = [];
      const colCount = selectColumsOne.length;
      for (let r = 0; r < colCount; r++) {
        if ((selectColumsOne[r] || '') === '') {
          if (['bottom', 'top'].indexOf(blankheaders) > -1) {
            finalColum.push(blankColum[r] ?? '');
          } else if (blankheaders === 'left') {
            finalColum.push(finalColum[r - 1] ?? '');
          } else {
            finalColum.push(selectColumsOne[r]);
          }
        } else {
          finalColum.push(selectColumsOne[r]);
        }
      }

      // Right-fill pass
      for (let r = selectColumsOne.length - 1; r > -1; r--) {
        if (selectColumsOne[r] === '') {
          if (blankheaders === 'right') finalColum[r] = finalColum[r + 1] ?? '';
        }
      }

      // Trim and clean — keep original punctuation (dots etc.) intact
      finalColum = finalColum
        .map((x) => (x || '').toString().trim())
        .map((item) => (item === '' ? 'Unnamed' : item));

      // Rename duplicates unless keepduplicatecolumns is enabled
      if (!duplicateColumns) {
        finalColum = this.renameDuplicateHeaders(finalColum);
      }

      return finalColum;
    },

    /**
     * Append a numeric suffix to duplicate header names.
     * E.g. ['Amount', 'Amount', 'Amount'] → ['Amount', 'Amount1', 'Amount2']
     */
    renameDuplicateHeaders(arr) {
      const count = {};
      arr.forEach(function (x, i) {
        if (arr.indexOf(x) !== i) {
          count[x] = count[x] || 0;
          count[x] += 1;
          let c = count[x];
          let j = c + 1;
          let k = x + (j - 1);
          while (arr.indexOf(k) !== -1) k = x + '(' + ++j + ')';
          arr[i] = k;
        }
      });
      return arr;
    },

    removeFileType(idx) {
      const entry = this.fileTypeEntries[idx];
      const removedKey = entry.key;
      Object.assign(entry, this.createEmptyEntry());

      // If the removed file was the base, clear mapping
      if (removedKey && removedKey === this.selectedBaseFileTypeKey) {
        this.selectedBaseFileTypeKey = '';
        this.mappingRows = [];
      }
      this.$forceUpdate();
    },

    onEntrySettingChanged(idx) {
      const entry = this.fileTypeEntries[idx];
      entry.ignorerows = Math.max(0, parseInt(entry.ignorerows, 10) || 0);
      entry.ignorecolumns = Math.max(0, parseInt(entry.ignorecolumns, 10) || 0);
      entry.ignorefooterrows = Math.max(0, parseInt(entry.ignorefooterrows, 10) || 0);
      if (entry.rawData && entry.rawData.length > 0) {
        entry.headers = this.extractHeaders(entry.rawData, entry);
        if (entry.key === this.selectedBaseFileTypeKey) {
          this.buildMappingRows();
        }
        this.$forceUpdate();
      }
    },

    // ──────────────────────────────────────────────
    // Section 2: Base File Selection
    // ──────────────────────────────────────────────
    onBaseFileChanged() {
      this.buildMappingRows();
    },

    /**
     * Clean a header for use as a base column name.
     * Removes dots and collapses extra whitespace.
     */
    cleanHeaderForBaseColumn(header) {
      return header.replace(/\./g, '').replace(/\s+/g, ' ').trim();
    },

    buildMappingRows() {
      const baseEntry = this.fileTypeEntries.find((ft) => ft.key === this.selectedBaseFileTypeKey);
      if (!baseEntry || !baseEntry.headers.length) {
        this.mappingRows = [];
        return;
      }

      this.mappingRows = baseEntry.headers.map((header, idx) => {
        // Build file type mappings with auto-match using original headers
        const fileTypeMappings = {};
        for (const ft of this.fileTypeEntries) {
          if (!ft.key || ft.status !== FILE_STATUS.SUCCESS) continue;
          if (ft.key === this.selectedBaseFileTypeKey) {
            fileTypeMappings[ft.key] = header; // original header from the file
          } else {
            const match = autoMatchHeader(header, ft.headers);
            fileTypeMappings[ft.key] = match || NULL_MAPPING_VALUE;
          }
        }

        return {
          id: idx,
          baseColumn: this.cleanHeaderForBaseColumn(header),
          dataType: inferDataType(header),
          userColumnMappingLabel: '',
          fileTypeMappings,
        };
      });

      // Reset primary keys
      this.selectedPrimaryKeys = [];
    },

    // ──────────────────────────────────────────────
    // Section 3: Mapping Table
    // ──────────────────────────────────────────────
    onBaseColumnRenamed() {
      // Update primary keys if a renamed column was selected
      this.cleanPrimaryKeys();
    },

    onFileTypeMappingChanged(rowIdx, fileTypeKey, value) {
      this.$set(this.mappingRows[rowIdx].fileTypeMappings, fileTypeKey, value);
    },

    /**
     * Get available header options for a file type dropdown in a given row.
     * Excludes headers already used in other rows for the same file type.
     */
    getAvailableHeaders(fileTypeKey, currentRowIdx) {
      const ft = this.fileTypeEntries.find((f) => f.key === fileTypeKey);
      if (!ft || ft.status !== FILE_STATUS.SUCCESS) return [NULL_MAPPING_VALUE];

      // Collect headers already selected in other rows for this file type
      const usedHeaders = new Set();
      this.mappingRows.forEach((row, idx) => {
        if (idx !== currentRowIdx) {
          const mapped = row.fileTypeMappings[fileTypeKey];
          if (mapped && mapped !== NULL_MAPPING_VALUE) {
            usedHeaders.add(mapped);
          }
        }
      });

      const available = ft.headers.filter((h) => !usedHeaders.has(h));

      // Always include the currently selected value (even if it would be "used")
      const currentValue = this.mappingRows[currentRowIdx]?.fileTypeMappings[fileTypeKey];
      if (currentValue && currentValue !== NULL_MAPPING_VALUE && !available.includes(currentValue)) {
        available.unshift(currentValue);
      }

      // Add the null option at top
      return [NULL_MAPPING_VALUE, ...available];
    },

    /**
     * Get available User Column Mapping options for a given row.
     * All options are always available regardless of selections in other rows.
     */
    getAvailableUserColMapOptions() {
      return this.userColMapOptions;
    },

    addRow() {
      const fileTypeMappings = {};
      for (const ft of this.fileTypeEntries) {
        if (!ft.key || ft.status !== FILE_STATUS.SUCCESS) continue;
        fileTypeMappings[ft.key] = NULL_MAPPING_VALUE;
      }

      this.mappingRows.push({
        id: this.mappingRows.length > 0 ? Math.max(...this.mappingRows.map((r) => r.id)) + 1 : 0,
        baseColumn: '',
        dataType: 'string',
        userColumnMappingLabel: '',
        fileTypeMappings,
      });
    },

    removeRow(rowIdx) {
      if (this.mappingRows.length <= 1) {
        this.$store.commit('sbError', 'At least one column must remain');
        return;
      }
      this.mappingRows.splice(rowIdx, 1);
      this.cleanPrimaryKeys();
    },

    // ──────────────────────────────────────────────
    // Section 4: Primary Keys
    // ──────────────────────────────────────────────
    cleanPrimaryKeys() {
      const current = this.baseColumnLabels;
      this.selectedPrimaryKeys = this.selectedPrimaryKeys.filter((pk) => current.includes(pk));
    },

    // ──────────────────────────────────────────────
    // Navigation
    // ──────────────────────────────────────────────
    onBack() {
      this.$emit('back');
    },

    onNextStep() {
      // Validation
      if (!this.validateBeforeNext()) return;

      // Build output payload
      const fileTypes = this.fileTypeEntries
        .filter((ft) => ft.key && ft.status === FILE_STATUS.SUCCESS)
        .map((ft) => ({
          key: ft.key,
          name: ft.name,
          description: '',
          ignorerows: parseInt(ft.ignorerows, 10) || 0,
          ignorecolumns: parseInt(ft.ignorecolumns, 10) || 0,
          ignorefooterrows: parseInt(ft.ignorefooterrows, 10) || 0,
          ignoredcolumns: [],
          unusedcolumns: [],
          createcolumns: false,
          blankcolumnheaders: ft.blankcolumnheaders || 'left',
          removeblankrows: ft.removeblankrows !== false,
          keepduplicatecolumns: !!ft.keepduplicatecolumns,
        }));

      const columnmap = this.mappingRows
        .filter((r) => r.baseColumn && r.baseColumn.trim() !== '')
        .map((row) => {
          const aliases = {};
          for (const ft of fileTypes) {
            const mapped = row.fileTypeMappings[ft.key];
            aliases[ft.key] = {
              header: mapped === NULL_MAPPING_VALUE ? null : (mapped || null),
              transform: null,
            };
          }

          // Resolve userColumnMappingLabel value to its text (supports free text from combobox)
          const rawLabel = row.userColumnMappingLabel;
          const userMapOption = this.userColMapOptions.find((o) => o.value === rawLabel);
          const userColMapText = userMapOption ? userMapOption.text : (typeof rawLabel === 'string' ? rawLabel : '');

          return {
            type: row.dataType,
            label: row.baseColumn,
            userColumnMappingLabel: userColMapText,
            fileTypeAliases: aliases,
          };
        });

      this.$emit('next', {
        fileTypes,
        columnmap,
        primaryKeys: [...this.selectedPrimaryKeys],
        selectedBaseFileTypeKey: this.selectedBaseFileTypeKey,
        // Pass along raw info for summary display
        fileTypeEntries: this.fileTypeEntries.map((ft) => ({
          key: ft.key,
          name: ft.name,
          fileName: ft.fileName,
          headers: ft.headers,
        })),
      });
    },

    validateBeforeNext() {
      // 1. All file types must be uploaded
      const allUploaded = this.fileTypeEntries.every((ft) => ft.status === FILE_STATUS.SUCCESS);
      if (!allUploaded) {
        this.$store.commit('sbError', 'Please upload a file for every file type');
        return false;
      }

      // 2. Base file must be selected
      if (!this.selectedBaseFileTypeKey) {
        this.$store.commit('sbError', 'Please select a base file');
        return false;
      }

      // 3. Every row must have a non-empty base column
      const emptyBaseCol = this.mappingRows.some((r) => !r.baseColumn || r.baseColumn.trim() === '');
      if (emptyBaseCol) {
        this.$store.commit('sbError', 'Every row must have a Base Column name');
        return false;
      }

      // 4. Validate each label
      for (const row of this.mappingRows) {
        const result = validateLabel(row.baseColumn);
        if (!result.valid) {
          this.$store.commit('sbError', `Column "${row.baseColumn}": ${result.message}`);
          return false;
        }
      }

      // 5. Check unique labels (base columns)
      const labels = this.mappingRows.map((r) => r.baseColumn);
      const uniqueLabels = new Set(labels);
      if (uniqueLabels.size !== labels.length) {
        this.$store.commit('sbError', 'Duplicate labels found in columnmap');
        return false;
      }

      // 6. Per file type: at least one column mapped (not "-")
      const successFts = this.fileTypeEntries.filter((ft) => ft.key && ft.status === FILE_STATUS.SUCCESS);
      for (const ft of successFts) {
        const hasMapped = this.mappingRows.some(
          (r) => r.fileTypeMappings[ft.key] && r.fileTypeMappings[ft.key] !== NULL_MAPPING_VALUE
        );
        if (!hasMapped) {
          this.$store.commit('sbError', `At least one column must be mapped for "${ft.name}"`);
          return false;
        }
      }

      // 7. At least one primary key
      if (this.selectedPrimaryKeys.length === 0) {
        this.$store.commit('sbError', 'Please select at least one primary key');
        return false;
      }

      return true;
    },
  },
  beforeDestroy() {
    this.fileTypeEntries = [];
    this.mappingRows = [];
    this.selectedPrimaryKeys = [];
  },
};
</script>

<style lang="scss" scoped>
@import './StepColumnMappingV2.scss';
</style>
